/**
 * "What to close this week" helpers (blueprint §7 C22–C24). Pure.
 *
 * The priority-actions endpoint has no workload or date fields; its `meta` is
 * "{component or product} / {scope} / {age}" ("SPOSharingSettings / 3
 * entities / 12d", "Identity / 6 GA accounts / Open"). The Workload column
 * maps the first segment (C22). The Status column carries the row's context
 * (queueStatus): the age as a phrase ("Drifted 12 days ago"), or the scope
 * when the row has no age ("6 GA accounts"), never an invented date (C23).
 * The endpoint caps the list and returns no total, so no copy claims "of N
 * open" (C24, D30).
 */
import type { Pillar, PriorityAction } from '@/lib/posture-brief'
import { getWorkloadForDriftComponent } from '@/lib/workloadMapping'
import { getWorkloadForProduct, type M365Workload } from '@/types/standardsCompliance'

/** The pillar's display name in queue copy (D23: the compliance pillar is "Baseline"). */
export const QUEUE_PILLAR_LABEL: Record<Pillar, string> = {
  compliance: 'Baseline',
  bpa: 'Best Practice',
  identity: 'Identity',
}

const WORKLOAD_LABEL: Record<M365Workload | 'Intune', string> = {
  Identity: 'Entra ID',
  Exchange: 'Exchange',
  Teams: 'Teams',
  SharePoint: 'SharePoint',
  Defender: 'Defender',
  Intune: 'Intune',
  Other: 'Other',
}

export interface ActionMeta {
  /** Display workload ("Entra ID", "Exchange", "Other"). */
  workload: string
  /** The scope segment ("3 entities"), or null. */
  scope: string | null
  /** The age or state segment ("12d", "today", "Open"); "Open" when the meta carries none (every queue row is open). */
  status: string
}

function workloadOf(segment: string, pillar: Pillar): M365Workload | 'Intune' {
  // The heatmap buckets Intune under Defender (workloadMapping.ts); a row's label names Intune itself.
  if (/^intune/i.test(segment)) return 'Intune'
  const byProduct = getWorkloadForProduct(segment)
  if (byProduct !== 'Other') return byProduct
  const byComponent = getWorkloadForDriftComponent(segment)
  if (byComponent !== 'Other') return byComponent
  // Every identity action is about Entra ID accounts (IdentityActionTemplates).
  return pillar === 'identity' ? 'Identity' : 'Other'
}

export function parseActionMeta(meta: string | null | undefined, pillar: Pillar): ActionMeta {
  const parts = (meta ?? '')
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean)
  const first = parts[0] ?? ''
  return {
    workload: WORKLOAD_LABEL[workloadOf(first, pillar)],
    scope: parts.length >= 3 ? parts[1] : null,
    status: parts.length >= 3 ? parts[parts.length - 1] : 'Open',
  }
}

/** "today", "1 day ago", "12 days ago" from the meta's age segment ("today", "1d", "12d"), or null for anything else. */
function agePhrase(segment: string): string | null {
  if (/^today$/i.test(segment)) return 'today'
  const m = /^(\d+)d$/i.exec(segment)
  if (!m) return null
  const n = Number(m[1])
  return n === 0 ? 'today' : n === 1 ? '1 day ago' : `${n.toLocaleString('en-US')} days ago`
}

/**
 * The Status cell (mockup 01: "Drifted Tue 14:20", "Failing since Sep 22", "20 members"), from what the meta
 * carries. The age is the newest detection in the action's group (DriftActionMapper and BpaActionMapper
 * BuildMeta), so a group of several reads "Last drifted …" or "Last detected …". Identity actions carry no age
 * ("Open"), so their scope ("6 GA accounts") is the context. A meta without segments is shown as given, never
 * "Open" alone.
 */
export function queueStatus(meta: string | null | undefined, pillar: Pillar): string {
  const parts = (meta ?? '')
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean)
  if (parts.length >= 3) {
    const scope = parts[1]
    const age = agePhrase(parts[parts.length - 1])
    if (age) {
      const count = Number((/^(\d[\d,]*)\b/.exec(scope)?.[1] ?? '1').replace(/,/g, ''))
      const verb = pillar === 'compliance' ? 'drifted' : 'detected'
      return `${count > 1 ? `Last ${verb}` : `${verb.charAt(0).toUpperCase()}${verb.slice(1)}`} ${age}`
    }
    return scope
  }
  if (parts.length === 2) return parts[1]
  return meta?.trim() || '—'
}

/**
 * The queue footer's link per switcher view: the pillar page that lists every open item, named for what it lists
 * (no count, D30). "All" has none: no page lists every pillar's items.
 */
export const QUEUE_FULL_LIST: Record<'all' | Pillar, { label: string; href: string } | null> = {
  all: null,
  compliance: { label: 'All open drifts', href: '/configuration-drift?status=Open' },
  bpa: { label: 'All failing controls', href: '/security?tab=best-practices' },
  identity: { label: 'All people needing attention', href: '/user-insights' },
}

/** "Turn the admin MFA policy back on first." from a ranked action title, or null without one. */
export function firstStep(title: string | null | undefined): string | null {
  const t = title?.trim().replace(/[.,;:!?]+$/, '')
  if (!t) return null
  return `${t.charAt(0).toUpperCase()}${t.slice(1)} first.`
}

/** "1 resource has", "304 resources have". */
export function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`
}

/** Client-side search over the loaded rows: title and meta, case-insensitive. */
export function searchActions(rows: ReadonlyArray<PriorityAction>, query: string): PriorityAction[] {
  const q = query.trim().toLowerCase()
  if (!q) return [...rows]
  return rows.filter((r) => `${r.title} ${r.meta ?? ''}`.toLowerCase().includes(q))
}

/** The table description (C24): no "of N", the endpoint returns no total. */
export function queueDescription(count: number): string {
  if (count <= 0) return 'Ranked by how much each item moves your score.'
  return count === 1 ? 'The 1 item that moves your score most.' : `The ${count} items that move your score most.`
}

/** The footer line: what is shown, and the order. */
export function queueFooter(shown: number, loaded: number): string {
  const noun = loaded === 1 ? 'item' : 'items'
  return shown === loaded ? `Showing ${loaded} ${noun}, ranked by impact.` : `Showing ${shown} of ${loaded} ${noun}, ranked by impact.`
}
